import React, { useEffect, useRef, useState } from 'react';
import Modal from '@mui/material/Modal';
import { Box } from '@mui/material';
import PropTypes from 'prop-types'
import { styles } from './ModalPopupComponent.style';
import { ErrorCardNew } from '../ErrorCard/ErrorCardNew';
import { metricCardErrorStore } from '@src/store/store';
import { Main_Header, MAX_TIN_LIMIT, Sub_Header } from './constant';

export interface ModelPopupData {
    type: string;
    title: string;
    message: string;
    titleName: string;
    dontShowText: string;
    cancelText: string;
    confirmText: string;
    checked: boolean;
    color: string;
    width: string;
    cssClassName: string;
    cardData: any;
};

const ModalPopupComponent = ({ show, data, onClose, onContinue, noPadding = false, noMarginButtonBlock=false, hasCrossIcon=false, taxIdLink=false, disableBackdropClose=false }) => {
    const [dontShowAgain, setDontShowAgain] = useState(data.checked);
    const [isContinueHovered, setIsContinueHovered] = useState(false);
    const [isCancelHovered, setIsCancelHovered] = useState(false);
    const [isCopiedTextValid, setIsCopiedTextValid] = useState(true);
    const contentEditableRef = useRef<HTMLDivElement>(null); // Ref for the contentEditable element
    const [numbers, setNumbers] = useState<string[]>([]); // Store numbers as an array
    const { setErrorMessage } = metricCardErrorStore();
    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
    const isUpdatingRef = useRef(false);
    const lastInputLengthRef = useRef(0); // Track last input length
    const [errorType, setErrorType] = useState('error'); // Add state for error type

    const onHandleCancel = (val?: any) => {
        // Reset all input values when modal closes
        setNumbers([]);
        setIsCopiedTextValid(true);
        if (contentEditableRef.current) {
            contentEditableRef.current.textContent = '';
        }
        onClose({ ...data, type: val });
    };

    const onHandleContinue = (val:any = undefined) => {
        if(val === 'multiTin') {
            onContinue(false, numbers, val);
            // Reset all input values after applying
            setNumbers([]);
            setIsCopiedTextValid(true);
            if (contentEditableRef.current) {
                contentEditableRef.current.textContent = '';
            }
        } else {
            onContinue(dontShowAgain, data);
        }
    }

     // Function to validate and extract valid 9-digit numbers
     const extractValidNumbers = (input: string) => {
        const allNumbers = input.split(/[\s,]+/).filter(Boolean); // Split input by spaces or commas
        const validNumbers = allNumbers.filter((num) => /^\d{9}$/.test(num)); // Extract valid 9-digit numbers
        const hasInvalidNumbers = allNumbers.some((num) => !/^\d{9}$/.test(num)); // Check for invalid numbers
        return { validNumbers, hasInvalidNumbers };
    };

    // Function to set cursor to end of content
    const setCursorToEnd = () => {
        const element = contentEditableRef.current;
        if (element) {
            const range = document.createRange();
            const selection = window.getSelection();
            range.selectNodeContents(element);
            range.collapse(false);
            selection?.removeAllRanges();
            selection?.addRange(range);
        }
    };

    // Enhanced function to handle manual input with auto-spacing after 9 digits
    const handleInput = (event: React.FormEvent<HTMLDivElement>) => {
        const inputElement = event.currentTarget;
        let inputText = inputElement.textContent || '';
        
        
        const { validNumbers, hasInvalidNumbers } = extractValidNumbers(inputText);


        // Update validation state
        if (inputText === '' || (validNumbers.length > 0 && !hasInvalidNumbers)) {
            setIsCopiedTextValid(true);
        } else {
            setErrorType('error');
            setIsCopiedTextValid(false);
        }

        // Update numbers state
        setNumbers(validNumbers);

        // Auto-format when user completes a 9-digit number
        if (validNumbers.length > 0) {
            // const currentCursorPosition = getCursorPosition();
            const lastChar = inputText[inputText.length - 1];
            const isDigit = /\d/.test(lastChar);

            // Check if we just completed a 9-digit number
            const wordsInInput = inputText.split(/\s+/).filter(Boolean);
            const lastWord = wordsInInput[wordsInInput.length - 1];

            if (isDigit && lastWord && lastWord.length === 9 && /^\d{9}$/.test(lastWord)) {
                // add spacing after  9-digit number
                setTimeout(() => {
                    if (contentEditableRef.current) {
                        const formattedText = validNumbers.map((num, index) => 
                            index === 0 ? num : `  ${num}`
                        ).join('    ');

                        // Add extra spaces at the end for next input
                        contentEditableRef.current.textContent = formattedText + '    ';
                        setCursorToEnd();
                    }
                }, 10);
            }
        }
    };

    // Remove onBlur formatting - no longer needed
    const handleBlur = () => {
        // Keep for any other blur-related logic if needed
    };

    // Allow natural editing on focus
    const handleFocus = () => {
        // Clear any pending formatting
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
    };

    // Handle keyboard events for better user experience
    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        // Allow all natural keyboard input including spaces
        // Don't prevent any default behavior
    };

    // Enhanced paste handler to preserve existing values
    const handlePaste = (event: React.ClipboardEvent<HTMLDivElement>) => {
        try {
            event.preventDefault();
            const clipboardData = event.clipboardData.getData('text');

            const { validNumbers: newValidNumbers, hasInvalidNumbers } = extractValidNumbers(clipboardData);

            if (newValidNumbers.length > 0) {
                // Calculate total numbers after this paste operation
                const totalNumbersAfterPaste = numbers.length + newValidNumbers.length;

                // Check if total would exceed limit
                if(totalNumbersAfterPaste > MAX_TIN_LIMIT) { 
                    setErrorType('limit_error');
                    setIsCopiedTextValid(false);
                }

                // Combine existing numbers with new pasted numbers
                setNumbers((prevNumbers) => {
                    const combinedNumbers = [...prevNumbers, ...newValidNumbers];
                    const uniqueNumbers = Array.from(new Set(combinedNumbers)); // Remove duplicates
                    return uniqueNumbers;
                });

                // Set validation state - if there are invalid numbers, show warning
                if (hasInvalidNumbers && !(totalNumbersAfterPaste > MAX_TIN_LIMIT)) {
                    setIsCopiedTextValid(false);
                    setErrorType('warning');
                } else if(!(totalNumbersAfterPaste > MAX_TIN_LIMIT)) {
                    setIsCopiedTextValid(true);
                }

                // Update content with all numbers (existing + new)
                if (contentEditableRef.current) {
                    const allNumbers = [...numbers, ...newValidNumbers];
                    const uniqueNumbers = Array.from(new Set(allNumbers));
                    // Format without leading spaces for the first number
                    const formattedText = uniqueNumbers.map((num, index) => 
                        index === 0 ? num : `  ${num}`
                    ).join('    ');
                    contentEditableRef.current.textContent = formattedText;
                    setCursorToEnd();
                }
            } else if (clipboardData.trim() === '') {
                // Only clear if pasting empty content and user intended to clear
                if (numbers.length === 0) {
                    setIsCopiedTextValid(true);
                    setNumbers([]);
                    if (contentEditableRef.current) {
                        contentEditableRef.current.textContent = '';
                    }
                }
            } else {
                // Invalid data pasted - show warning for paste events
                setErrorType('warning');
                setIsCopiedTextValid(false);
            }
        } catch (error) {
            console.error('Error handling paste event:', error);
        }
    };

    // Reset refs and input values when modal opens/closes
    useEffect(() => {
        if (show) {
            lastInputLengthRef.current = 0;
            isUpdatingRef.current = false;
            // Reset input values when modal opens
            setNumbers([]);
            setIsCopiedTextValid(true);
            if (contentEditableRef.current) {
                contentEditableRef.current.textContent = '';
            }
        }
    }, [show]);

    // Cleanup timer on component unmount
    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, []);


    const disableCHeck = () => { 
        // Button should be disabled if: no numbers OR invalid text with error type (not warning)
        const shouldDisable = numbers.length === 0 || (!isCopiedTextValid && (errorType === 'error' || errorType === 'limit_error'));
        return shouldDisable;
    }
    const getWarningIcon = () => {
        return(
            <svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 80 80" fill="none">
<rect width="80" height="80" rx="40" fill="#FEF7E5"/>
<path d="M25.9737 53.125C25.7064 53.125 25.4633 53.0582 25.2446 52.9245C25.0258 52.7908 24.8557 52.6146 24.7341 52.3958C24.6126 52.1771 24.5458 51.9401 24.5336 51.6849C24.5215 51.4297 24.5883 51.1806 24.7341 50.9375L38.2237 27.6042C38.3696 27.3611 38.5579 27.1788 38.7888 27.0573C39.0197 26.9358 39.2567 26.875 39.4998 26.875C39.7428 26.875 39.9798 26.9358 40.2107 27.0573C40.4416 27.1788 40.63 27.3611 40.7758 27.6042L54.2654 50.9375C54.4112 51.1806 54.4781 51.4297 54.4659 51.6849C54.4538 51.9401 54.3869 52.1771 54.2654 52.3958C54.1439 52.6146 53.9737 52.7908 53.755 52.9245C53.5362 53.0582 53.2932 53.125 53.0258 53.125H25.9737ZM39.4998 48.75C39.913 48.75 40.2593 48.6102 40.5388 48.3307C40.8184 48.0512 40.9581 47.7049 40.9581 47.2917C40.9581 46.8785 40.8184 46.5321 40.5388 46.2526C40.2593 45.9731 39.913 45.8333 39.4998 45.8333C39.0866 45.8333 38.7402 45.9731 38.4607 46.2526C38.1812 46.5321 38.0414 46.8785 38.0414 47.2917C38.0414 47.7049 38.1812 48.0512 38.4607 48.3307C38.7402 48.6102 39.0866 48.75 39.4998 48.75ZM39.4998 44.375C39.913 44.375 40.2593 44.2352 40.5388 43.9557C40.8184 43.6762 40.9581 43.3299 40.9581 42.9167V38.5417C40.9581 38.1285 40.8184 37.7821 40.5388 37.5026C40.2593 37.2231 39.913 37.0833 39.4998 37.0833C39.0866 37.0833 38.7402 37.2231 38.4607 37.5026C38.1812 37.7821 38.0414 38.1285 38.0414 38.5417V42.9167C38.0414 43.3299 38.1812 43.6762 38.4607 43.9557C38.7402 44.2352 39.0866 44.375 39.4998 44.375Z" fill="#BD5000"/>
</svg>
        )
    }

    return (<div className='modal-popup-component'>
        <Modal BackdropProps={{ style: { background: 'rgba(0, 0, 0, 0.32)' } }} open={show} onClose={(event, reason) => { if (disableBackdropClose && (reason === 'backdropClick' || reason === 'escapeKeyDown')) return; onHandleCancel(0); }} aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description">
            <Box sx={{ ...(taxIdLink ? styles.taxIdbox : styles.box), ...(data?.boxStyle || {}) }}>
           {!taxIdLink && <div style={styles.crossIconTopRight} onClick={() => onHandleCancel(0)}>&#x2715;</div>}
            {data?.hasWarningIcon && !taxIdLink &&  <div style={styles.iconContainer}>
             {/* <img
                style={styles.iconContainer.warningIcon}
                src="assets/images/warning-icon.svg"
                alt=""
            /> */}
            {getWarningIcon()}
        </div>}
              { !taxIdLink ? <div style={styles.dialog}>
                     <div style={{ ...styles.headerSection, ...(data?.hasWarningIcon ? { justifyContent: 'center' } : {}) }}>
                        <h3 style={{ ...styles.header, padding: noPadding ? '0' : styles.header.padding, ...(data?.hasWarningIcon ? { textAlign: 'center' } : {}) }}>{data?.titleName}
                            {data?.title ? <><br /><span style={{ 'color': data?.color }}>{data?.title}</span></> : ""}</h3>
                    </div>
                    <div style={{ ...styles.content, 'color': '#757588', ...(data?.hasWarningIcon ? { textAlign: 'center' } : {}) }}><div>{data?.message}</div></div>
                    <div style={styles.belowBlock}>
                        {(data?.dontShowText) ? <div style={styles.checkboxBlock}>
                            <input type="checkbox" style={styles.dontShowCheckbox} value={data?.title} defaultChecked={data?.checked}
                                onChange={(event) => setDontShowAgain(event.target.checked)} />
                            <p style={styles.modalPopupText}>{data?.dontShowText}</p>
                        </div> : <></>}
                        <div style={styles.buttonDivider}></div>
                        <div style={styles.buttonSection}>
                            {data.customStylingForButtons ? <>
                                <button 
                                    onMouseEnter={() => setIsCancelHovered(true)}
                                    onMouseLeave={() => setIsCancelHovered(false)}
                                    style={isCancelHovered ? {...styles.cancelButton, ...styles.cancelButtonHovered} : styles.cancelButton} 
                                    onClick={() => onHandleCancel(1)}>
                                    {data?.cancelText}
                                </button>
                                <button
                                    onMouseEnter={() => setIsContinueHovered(true)}
                                    onMouseLeave={() => setIsContinueHovered(false)}
                                    style={isContinueHovered? {...styles.deleteButtonHovered, ...styles.continueButton}: styles.continueButton } 
                                    data-testid="Continue" 
                                    onClick={onHandleContinue} >
                                        {data?.confirmText}
                                </button>                             
                            </>: <> 
                                <button style={{ ...styles.noThanksBtn, ...(data?.btnOneStyle || {}) }} onClick={() => onHandleCancel(1)}>{data?.cancelText}</button>
                                <button style={{ ...styles.continueBtn, ...(data?.btnTwoStyle || {}) }} data-testid="Continue" onClick={onHandleContinue} >{data?.confirmText}</button>
                            </>}
                        </div>
                    </div>
                </div> : <div style={styles.taxIdDialog}>
                        <div style= {styles.taxHeaderContent}>
                        <div style={styles.taxIdDialogContent}>
                        <div style={styles.taxIdHeader}>{`${Main_Header}`}</div>
                        <div style={styles.taxIdSubHeader}>{`${Sub_Header}`} </div>
                        </div>
                        {hasCrossIcon && taxIdLink && <div style={styles.crossIcon} onClick={() => onHandleCancel(0)}>&#x2715;</div>}
                        </div>
                        {!isCopiedTextValid && <div style= {styles.taxIdErrorCard}>
                            <ErrorCardNew taxIdLink={true} errorType={errorType}/>
                        </div>}
                        <div
                          contentEditable="true"
                          ref={contentEditableRef}
                          style={styles.taxIdTextArea}
                          onPaste={handlePaste}
                          onInput={handleInput}
                          onBlur={handleBlur}
                          onFocus={handleFocus}
                          onKeyDown={handleKeyDown}
                          suppressContentEditableWarning={true}
                           >
                          {/* {getFormattedContent()}   */}
                      </div>
                      <div style={styles.divider}></div>
                      <div style={styles.taxIdButton}>
                      <button style={{ ...styles.taxCancelBtn, ...(data?.btnOneStyle || {}) }} onClick={() => onHandleCancel(1)}>{data?.cancelText}</button>
                      <button 
                        style={{ 
                          ...styles.continueBtn, 
                          ...(data?.btnTwoStyle || {}),
                          opacity: disableCHeck() ? 0.5 : 1,
                          cursor: disableCHeck() ? 'not-allowed' : 'pointer'
                        }} 
                        data-testid="Continue" 
                        onClick={() => !disableCHeck() && onHandleContinue('multiTin')}
                        disabled={disableCHeck()}
                      >
                        {data?.confirmText}
                      </button>  

                      </div>
                      </div>
                }
            </Box>
        </Modal>
    </div>);
};
ModalPopupComponent.propTypes = {
    show: PropTypes.bool.isRequired,
    data: PropTypes.any.isRequired,
    onClose: PropTypes.func.isRequired,
    onContinue: PropTypes.func.isRequired,
};
ModalPopupComponent.defaultValue = {
    show: false,
    data: {
        type: "modalPopupDefault",
        title: "You are being directed to the XXXX",
        message: "A new browser window will open with the XXXX.",
        titleName: "Don’t show me this again this session.",
        dontShowText: "",
        cancelText: "No Thanks, Stay Here.",
        confirmText: "Continue"
    },
    onClose: () => { },
    onContinue: () => { },
};
export default ModalPopupComponent;